<script setup lang="ts">
import dayjs from 'dayjs'
import { round } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import { Link } from '@inertiajs/vue3'
import { useHelpers } from '@/Composables/helpers'
import Breadcrumbs from '@/Components/Breadcrumbs.vue'
import ApplicationHead from '@/Components/Layout/ApplicationHead.vue'
import Rating from '@/Components/UI/Rating.vue'
import { BuildingStorefrontIcon, ShoppingBagIcon, StarIcon } from '@heroicons/vue/24/solid'
import { Review } from '@interfaces/review.interface'
import Pagination from '@/Components/Pagination.vue'

defineProps<{
    reviews: JsonResourcePaginated<Array<Review>>,
    summary: Array<any>,
    reviewsCount: number,
    reviewsRatingAverage: number,
}>()

const { t } = useI18n()
const { asset } = useHelpers()
</script>

<template>
    <div>
        <ApplicationHead :title="t('pages.reviews.title')" :description="t('pages.reviews.title')">
            <meta property="og:type" content="website">
            <meta property="og:image" :content="asset('/images/social-logo.png')">
        </ApplicationHead>

        <Breadcrumbs />

        <div class="px-4 py-16 mx-auto bg-white max-w-7xl sm:px-6 lg:px-8">
            <div class="lg:grid lg:grid-cols-12 lg:gap-x-8">
                <div class="lg:col-span-4">
                    <h1 class="text-3xl font-bold tracking-tight xl:text-4xl text-slate-900">{{ t('pages.reviews.title') }}</h1>

                    <div class="flex items-center mt-3">
                        <div>
                            <Rating :rating="reviewsRatingAverage" />
                            <p class="sr-only">{{ t('components.review.stars-count', { count: round(reviewsRatingAverage) }) }}</p>
                        </div>
                        <p class="ml-2 text-sm text-slate-900">{{ t('components.review.based-on-reviews', reviewsCount) }}</p>
                    </div>

                    <div class="mt-6">
                        <h2 class="sr-only">Review data</h2>

                        <dl class="space-y-3">
                            <div v-for="(item, itemIdx) in summary" :key="itemIdx" class="flex items-center text-sm">
                                <dt class="flex items-center flex-1">
                                    <p class="w-3 font-medium text-slate-900">{{ item.rating }}<span class="sr-only"> star reviews</span></p>
                                    <div aria-hidden="true" class="flex items-center flex-1 ml-1">
                                        <StarIcon class="flex-shrink-0 w-5 h-5 text-yellow-400" aria-hidden="true" />

                                        <div class="relative flex-1 ml-3">
                                            <div class="h-3 bg-gray-100 border border-gray-200 rounded-full"></div>

                                            <div v-if="item.percentage > 0" :style="{ width: `calc(${item.percentage}%)` }" class="absolute inset-y-0 bg-yellow-400 border border-yellow-400 rounded-full"></div>
                                        </div>
                                    </div>
                                </dt>
                                <dd class="w-10 ml-3 text-sm text-right tabular-nums text-slate-900">{{ item.percentage }}%</dd>
                            </div>
                        </dl>
                    </div>

                    <div class="mt-10">
                        <h2 class="text-lg font-medium text-slate-900">{{ t('pages.reviews.reviews-type') }}</h2>

                        <div class="mt-6 space-y-1 text-sm font-medium leading-6 bg-white rounded-lg text-slate-700">
                            <Link :class="['flex rounded-[10px] p-1', (route().params as any).type !== 'products' ? 'bg-gray-50' : 'hover:bg-gray-50']" :href="route('reviews', { type: 'shop' })">
                            <div class="flex items-center justify-center flex-none bg-white rounded-md shadow h-7 w-7 ring-1 ring-gray-900/10">
                                <BuildingStorefrontIcon class="w-5 h-5 fill-gray-400" aria-hidden="true" />
                            </div>

                            <div class="ml-3">{{ t('pages.reviews.shop') }}</div>
                            </Link>

                            <Link :class="['flex rounded-[10px] p-1', (route().params as any).type === 'products' ? 'bg-gray-50' : 'hover:bg-gray-50']" :href="route('reviews', { type: 'products' })">
                            <div class="flex items-center justify-center flex-none bg-white rounded-md shadow h-7 w-7 ring-1 ring-gray-900/10">
                                <ShoppingBagIcon class="w-5 h-5 fill-gray-400" aria-hidden="true" />
                            </div>

                            <div class="ml-3">{{ t('pages.reviews.products') }}</div>
                            </Link>
                        </div>
                    </div>
                </div>

                <div class="mt-16 lg:col-span-7 lg:col-start-6 lg:mt-0">
                    <h3 class="sr-only">Recent reviews</h3>

                    <div class="flow-root">
                        <div class="-my-12 divide-y divide-gray-200">
                            <div v-if="reviews.data.length === 0" class="text-center py-28">
                                <div class="mb-6 text-4xl">😊</div>
                                <p class="text-sm text-slate-700">{{ t('components.review.no-reviews') }}</p>
                            </div>

                            <template v-else>
                                <div v-for="review in reviews.data" :key="review.id" class="py-12">
                                    <div class="flex items-center text-sm gap-x-4">
                                        <Link v-if="review.product" :href="route('product', { slug: review.product.slug })">
                                        <div class="relative w-12 h-12 overflow-hidden bg-gray-200 rounded-lg">
                                            <picture>
                                                <source :data-srcset="review.product.image_card_webp_url" type="image/webp">
                                                <source :data-srcset="review.product.image_card_url" type="image/jpeg">

                                                <img :data-src="review.product.image_card_url" class="object-cover object-center w-full h-full lazyload" :alt="review.product.title">
                                            </picture>

                                            <div class="absolute inset-0 bg-black bg-opacity-[0.02] transition-colors group-hover:bg-opacity-[0.04]"></div>
                                        </div>
                                        </Link>

                                        <div v-else class="inline-block w-12 h-12 overflow-hidden bg-gray-100 rounded-full">
                                            <svg class="w-full h-full text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                                            </svg>
                                        </div>

                                        <div>
                                            <p class="font-bold text-slate-900">{{ review.name ?? t('components.review.anonymous') }}<time class="ml-4 font-normal text-slate-500" :datetime="dayjs(review.created_at).format('YYYY-MM-DD')">{{ dayjs(review.created_at).format('D MMM YYYY') }}</time></p>
                                            <Rating class="mt-1" :rating="review.rating"></Rating>
                                            <p class="sr-only">{{ t('components.review.stars-count', { count: round(review.rating) }) }}</p>
                                        </div>
                                    </div>

                                    <Link v-if="review.product" class="inline-block mt-4 text-sm font-bold" :href="route('product', { slug: review.product.slug })">
                                    {{ review.product.title }}
                                    </Link>

                                    <div :class="[review.product ? 'mt-2' : 'mt-4', 'space-y-6 text-base italic text-slate-600']">
                                        <p>{{ review.message ?? t('components.review.no-comment') }}</p>
                                    </div>

                                    <!-- {{ -- @if ($review -> answer)
                                        <div class="review review-child" >
                                            <div class="review-body" >
                                                <div class="row" >
                                                    <div class="col-12 col-md-auto" >
                                                        <div class="mb-6 avatar avatar-xxl mb-md-0" >
                                                            <picture>
                                                            <source srcset="{{ asset('/images/avatar-calypso.png') }} 1x, {{ asset('/images/avatar-calypso@2x.png') }} 2x" type="image/jpeg" />
                                                            <img src="{{ asset('/images/avatar-calypso.png') }}" alt="..." class="avatar-img rounded-circle">
                                                        </picture>
                                                    </div>
                                                </div>

                                                <div class="col-12 col-md">
                                                    <div class="mb-6 row">
                                                        <div class="col-12">
                                                            <span class="font-weight-bold">{{ t('Calypso team') }}</span>
                                                        </div>

                                                        <div class="col-12">
                                                            <span class="font-size-xs text-muted">
                                                                <time datetime="{{ $review->answer->created_at->format('Y-m-d') }}">{{ $review -> answer -> created_at -> formatLocalized('%e %b %Y') }}</time>
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <p class="mb-0 text-slate-500">{{ $review -> answer -> message }}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    @endif --}} -->
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <Pagination class="mt-16" :paginator="reviews.meta" />
        </div>
    </div>
</template>
